/**
 * Event date field — Event-Date Guarantee (rewritten 6 Aug 2026).
 *
 * Replaces the old JS-injected per-product "Date Required" line-item property
 * with ONE cart attribute: "Event date", stored in UK format (DD/MM/YYYY).
 *
 * - The same value is shared by the product page, cart page and cart drawer
 *   (inputs marked [data-event-date-input], rendered by
 *   snippets/event-date-guarantee.liquid).
 * - Past dates are rejected with an accessible error message.
 * - Changes persist immediately via /cart/update.js, and also submit with the
 *   cart form as a fallback via [data-event-date-hidden].
 * - The field is optional; nothing is ever forced.
 */
(function () {
  'use strict';

  var ATTR = 'Event date';
  var saved = null; // UK-format string once known ('' = none set)

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function ukToISO(uk) {
    var m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((uk || '').trim());
    return m ? m[3] + '-' + pad(+m[2]) + '-' + pad(+m[1]) : '';
  }

  function isoToUK(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return m ? m[3] + '/' + m[2] + '/' + m[1] : '';
  }

  function errorEl(input) {
    var id = input.getAttribute('data-error-target');
    return id ? document.getElementById(id) : null;
  }

  function setError(input, show) {
    var err = errorEl(input);
    if (!err) return;
    err.hidden = !show;
    if (show) {
      input.setAttribute('aria-invalid', 'true');
      var describedBy = input.getAttribute('aria-describedby') || '';
      if (describedBy.indexOf(err.id) === -1) {
        input.setAttribute('aria-describedby', (describedBy + ' ' + err.id).trim());
      }
    } else {
      input.removeAttribute('aria-invalid');
    }
  }

  function syncAll(uk) {
    var iso = ukToISO(uk);
    document.querySelectorAll('[data-event-date-input]').forEach(function (el) {
      if (el === document.activeElement) return; // never fight the user mid-entry
      if (el.value !== iso) el.value = iso;
      setError(el, false);
    });
    document.querySelectorAll('[data-event-date-hidden]').forEach(function (el) {
      el.value = uk;
    });
  }

  function persist(uk) {
    saved = uk;
    syncAll(uk);
    var payload = { attributes: {} };
    payload.attributes[ATTR] = uk;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(function () {
      /* Non-fatal: the hidden field still submits the value with the cart form. */
    });
  }

  function onChange(input) {
    var v = input.value;
    if (v && v < todayISO()) {
      setError(input, true);
      input.value = saved ? ukToISO(saved) : '';
      return;
    }
    setError(input, false);
    var uk = v ? isoToUK(v) : '';
    if (uk === (saved || '')) return; // nothing changed (e.g. blur after no edit)
    persist(uk);
  }

  function initInputs() {
    document.querySelectorAll('[data-event-date-input]').forEach(function (input) {
      input.min = todayISO();
      if (input.dataset.edReady) return;
      input.dataset.edReady = '1';
      input.addEventListener('change', function () { onChange(input); });
      input.addEventListener('blur', function () { onChange(input); });
      input.addEventListener('input', function () {
        // A date input's value is only non-empty once a COMPLETE date is present,
        // so this warns immediately when a past date is typed, without flashing
        // errors mid-entry. The value is only reverted on commit (change/blur).
        var v = input.value;
        if (v && v < todayISO()) {
          setError(input, true);
        } else {
          setError(input, false);
        }
      });
      if (saved && !input.value) input.value = ukToISO(saved);
    });
  }

  function boot() {
    // Fast source of truth: the liquid-rendered hidden field (cart page/drawer).
    var hidden = document.querySelector('[data-event-date-hidden]');
    if (hidden && hidden.value) saved = hidden.value;
    initInputs();
    if (saved) syncAll(saved);

    // Authoritative source: the cart itself (covers the product page too).
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        saved = (cart && cart.attributes && cart.attributes[ATTR]) || '';
        initInputs();
        syncAll(saved);
      })
      .catch(function () { /* leave whatever we already have */ });

    // The cart drawer re-renders its contents after cart changes — re-bind then.
    var mo = new MutationObserver(function () {
      initInputs();
      if (saved !== null) syncAll(saved);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
